import React from 'react';
import { Snowflake, Sun, Cloud, CloudRain, Thermometer } from 'lucide-react';
import PageHero, { Section, InfoCard, CTABlock } from '../components/PageHero';

const seasons = [
  { icon: Snowflake, title: 'Winter (Dec–Feb)', temp: '−15°C to −5°C', desc: 'Snow-covered cities, indoor central heating in all hostels. Perfect for those who’ve dreamed of a white Christmas.' },
  { icon: Cloud, title: 'Spring (Mar–May)', temp: '0°C to 15°C', desc: 'Snow melts, trees bloom, and Russians step out to celebrate long-awaited sunshine.' },
  { icon: Sun, title: 'Summer (Jun–Aug)', temp: '15°C to 28°C', desc: 'Long daylight (up to 18 hours in the north), warm weather, and “belye nochi” white nights in Saint Petersburg.' },
  { icon: CloudRain, title: 'Autumn (Sep–Nov)', temp: '5°C to 15°C', desc: 'Golden leaves everywhere, mild rain, and one of the most beautiful times in Russian cities.' },
];

const cities = [
  { name: 'Moscow', min: '−10°C', max: '25°C', notes: 'Cold, snowy winters; warm, pleasant summers.' },
  { name: 'Saint Petersburg', min: '−7°C', max: '23°C', notes: 'Milder than Moscow; famous for white nights in June.' },
  { name: 'Kazan', min: '−13°C', max: '26°C', notes: 'Classic continental — cold winters, warm summers.' },
  { name: 'Rostov-on-Don', min: '−5°C', max: '30°C', notes: 'Warmest MBBS destination — short winters.' },
  { name: 'Vladivostok', min: '−15°C', max: '20°C', notes: 'Coastal humid climate near Sea of Japan.' },
  { name: 'Novosibirsk / Siberia', min: '−20°C', max: '25°C', notes: 'Cold but very dry — easier to tolerate than expected.' },
];

export default function Weather() {
  return (
    <>
      <PageHero eyebrow="Climate Guide" title="Weather in" highlight="Russia"
        subtitle="Russia is BIG. From Kaliningrad to Vladivostok, the climate varies hugely. Here's what to expect city-by-city for Indian students moving to Russia."
        breadcrumbs={[{ label: 'Weather in Russia' }]} />

      <Section eyebrow="Seasons" title="Four Seasons of" highlight="Russia">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {seasons.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-5 card-lift">
              <div className="w-11 h-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><s.icon className="w-5 h-5" /></div>
              <h4 className="font-serif text-lg font-bold text-primary mt-4">{s.title}</h4>
              <div className="text-xs font-bold text-brand mt-1">{s.temp}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="City Comparison" title="MBBS Cities by" highlight="Climate" muted>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-primary text-white">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-left px-4 py-3 font-semibold">Winter Low</th>
                <th className="text-left px-4 py-3 font-semibold">Summer High</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/40'}>
                  <td className="px-4 py-3 font-semibold text-primary">{c.name}</td>
                  <td className="px-4 py-3 text-brand font-bold">{c.min}</td>
                  <td className="px-4 py-3 text-brand font-bold">{c.max}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <CTABlock title="Not sure how you'll handle Russian winters?" subtitle="All hostels come with central heating (25–28°C indoors). Winter jackets are cheaper in Russia than in India." />
    </>
  );
}
